import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// form
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
// @mui
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Tooltip,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { convertToFormattedString } from 'utils/helper';

// ----------------------------------------------------------------------

RHFSelect.propTypes = {
  name: PropTypes.string,
  native: PropTypes.bool,
  children: PropTypes.node,
  helperText: PropTypes.node,
  maxHeight: PropTypes.number,
  rules: PropTypes.object,
};

export function RHFSelect({ name, native, children, helperText, maxHeight = 220, rules, ...other }) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          select
          fullWidth
          SelectProps={{
            native,
            MenuProps: {
              PaperProps: {
                sx: {
                  ...(!native && {
                    px: 1,
                    maxHeight: typeof maxHeight === 'number' ? maxHeight : 'unset',
                    '& .MuiMenuItem-root': {
                      px: 1,
                      borderRadius: 0.75,
                      typography: 'body2',
                      textTransform: 'capitalize',
                    },
                  }),
                },
              },
            },
            sx: { textTransform: 'capitalize' },
          }}
          error={!!error}
          helperText={error ? error?.message : helperText}
          {...other}
        >
          {children}
        </TextField>
      )}
    />
  );
}

// ----------------------------------------------------------------------

RHFMultiSelect.propTypes = {
  name: PropTypes.string,
  chip: PropTypes.bool,
  label: PropTypes.string,
  options: PropTypes.array,
  checkbox: PropTypes.bool,
  placeholder: PropTypes.string,
  helperText: PropTypes.node,
  limitTags: PropTypes.number,
  onCallBack: PropTypes.func,
  sx: PropTypes.object,
  required: PropTypes.bool,
};

export function RHFMultiSelect({
  name,
  chip,
  label,
  options,
  checkbox,
  placeholder,
  helperText,
  limitTags,
  onCallBack,
  sx,
  required,
  ...other
}) {
  const { control, setValue } = useFormContext();

  const { fields } = useFieldArray({
    control,
    name,
  });
  const fieldValue = fields || [];
  const areAllSelected = options.length > 0 && fieldValue.length === options.length;

  const [labelWidth, setLabelWidth] = useState(100);

  useEffect(() => {
    const updateLabelWidth = () => {
      const labelElement = document.getElementById(`meal-label`);
      if (labelElement) {
        setLabelWidth(labelElement.offsetWidth);
      }
    };

    // Initial calculation
    updateLabelWidth();

    // Listen for window resize events
    window.addEventListener('resize', updateLabelWidth);

    // Remove the event listener on component unmount
    return () => {
      window.removeEventListener('resize', updateLabelWidth);
    };
  }, []);

  const handleOnChange = (event) => {
    const { value: selectedItem } = event.target;
    if (selectedItem?.includes('all')) {
      const allOptions = options?.map((item) => item.value);
      const newValue = areAllSelected ? [] : allOptions;

      setValue(name, newValue);

      if (onCallBack) onCallBack(newValue);
    } else {
      setValue(name, selectedItem);
      if (onCallBack) onCallBack(selectedItem);
    }
  };

  const renderValues = (selectedItemValues) => {
    const selectedItems = options?.filter((item) => selectedItemValues?.includes(item?.value));
    const containerWidth = labelWidth; // Use the label width for calculation

    if (!containerWidth || !selectedItems?.length) {
      return null;
    }

    const labels = selectedItems.map((item) => item?.label);

    const labelsFit = (labelProps) => {
      // eslint-disable-next-line no-unsafe-optional-chaining
      const totalWidth = labelProps.reduce((sum, lbl) => sum + lbl?.length * 20, 0); // Assuming average character width as 10
      return totalWidth <= containerWidth;
    };

    if (labelsFit(labels)) {
      // All labels fit
      return (
        <Box
          sx={{ display: 'flex', flexWrap: 'nowrap', gap: 0.5, height: '25px', minWidth: '40px' }}
        >
          {labels.map((item) => (
            <Chip key={item.value} size="small" label={item} />
          ))}
        </Box>
      );
    }
    // Display as many labels as possible and show "more" label
    const visibleLabels = [];
    let currentWidth = 10;
    let index = 0;

    while (currentWidth <= containerWidth && index < labels.length) {
      currentWidth += labels[index].length * 20; // Assuming average character width as 10
      if (currentWidth <= containerWidth) {
        visibleLabels.push(labels[index]);
      }
      // eslint-disable-next-line no-plusplus
      index++;
    }

    const remainingNotVisibleLabels = selectedItems?.filter(
      (item) => !visibleLabels.includes(item?.label)
    );

    const remainingLabels = labels.slice(index);
    const limitedTagsCount = remainingLabels.length + 1;

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {visibleLabels.map((item) => (
          <Chip key={item} size="small" label={item} />
        ))}
        <Tooltip
          title={remainingNotVisibleLabels
            ?.map((item) => convertToFormattedString(item?.label))
            .join(', ')}
          placement="top"
          sx={{ cursor: 'pointer' }}
        >
          <Chip size="small" sx={{ fontWeight: 600 }} label={`+${limitedTagsCount} more`} />
        </Tooltip>
      </Box>
    );
  };

  const isOptionsExist = options?.length > 0 || false;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <FormControl sx={sx}>
          {label && (
            <InputLabel id={name}>
              {label}
              {required && <span style={{ color: 'red' }}>&nbsp; *</span>}
            </InputLabel>
          )}

          <Select
            {...field}
            multiple
            displayEmpty={!!placeholder}
            labelId={name}
            input={<OutlinedInput fullWidth label={label} error={!!error} />}
            onChange={handleOnChange}
            renderValue={renderValues}
            MenuProps={{
              PaperProps: {
                sx: { px: 1, maxHeight: 280 },
              },
            }}
            {...other}
          >
            {placeholder && (
              <MenuItem
                disabled
                value=""
                sx={{
                  py: 1,
                  px: 2,
                  borderRadius: 0.75,
                  typography: 'body2',
                }}
              >
                <em> {placeholder} </em>
              </MenuItem>
            )}
            {isOptionsExist && (
              <MenuItem
                value="all"
                sx={{
                  py: 1,
                  px: 2,
                  borderRadius: 0.75,
                  typography: 'body2',
                  ...(areAllSelected && {
                    fontWeight: 'fontWeightMedium',
                  }),
                  ...(checkbox && {
                    p: 0.25,
                  }),
                }}
              >
                {checkbox && <Checkbox disableRipple size="small" checked={areAllSelected} />}
                Select All
              </MenuItem>
            )}

            {checkbox && isOptionsExist && (
              <Divider
                sx={{
                  borderStyle: 'dashed',
                }}
              />
            )}

            {!isOptionsExist && (
              <MenuItem value="No Options" disabled>
                No Options
              </MenuItem>
            )}

            {isOptionsExist &&
              options.map((option) => {
                const selected = field?.value?.includes(option.value);

                return (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{
                      py: 1,
                      px: 2,
                      borderRadius: 0.75,
                      typography: 'body2',
                      ...(selected && {
                        fontWeight: 'fontWeightMedium',
                      }),
                      ...(checkbox && {
                        p: 0.25,
                      }),
                    }}
                  >
                    {checkbox && <Checkbox disableRipple size="small" checked={selected} />}

                    {option.label}
                  </MenuItem>
                );
              })}
          </Select>

          {(!!error || helperText) && (
            <FormHelperText error={!!error}>{error ? error?.message : helperText}</FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
}

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export function RHFAutocompleteMultiSelect({
  name,
  label,
  options = [],
  getOptionLabel = (option) => option?.label || option,
  ...other
}) {
  const { control } = useFormContext();
  const ALL_OPTION = '__all__';

  // Normalize options
  const normalizedOptions = options.map((opt) =>
    typeof opt === 'string' ? { value: opt, label: opt } : opt
  );
  const extendedOptions = [{ value: ALL_OPTION, label: 'All' }, ...normalizedOptions];

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={[]}
      render={({ field: { value = [], onChange, ...field } }) => {
        // Ensure value is array of strings (values only)
        const selectedValues = Array.isArray(value) ? value : [];

        const isAllSelected =
          normalizedOptions.length > 0 &&
          normalizedOptions.every((opt) => selectedValues.includes(opt.value));

        const handleChange = (event, newValue) => {
          const lastSelected = newValue[newValue.length - 1];

          if (lastSelected?.value === ALL_OPTION) {
            if (isAllSelected) {
              onChange([]);
            } else {
              onChange(normalizedOptions.map((opt) => opt.value));
            }
          } else {
            const filtered = newValue.filter((v) => v?.value !== ALL_OPTION).map((v) => v.value);
            onChange(filtered);
          }
        };

        return (
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={extendedOptions}
            getOptionLabel={(option) =>
              option?.value === ALL_OPTION ? 'All' : getOptionLabel(option)
            }
            value={normalizedOptions.filter((opt) => selectedValues.includes(opt.value))}
            onChange={handleChange}
            isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={
                    option.value === ALL_OPTION
                      ? isAllSelected
                      : selectedValues.includes(option.value)
                  }
                />
                {option?.value === ALL_OPTION ? 'All' : getOptionLabel(option)}
              </li>
            )}
            renderInput={(params) => <TextField {...params} label={label} />}
            {...field}
            {...other}
          />
        );
      }}
    />
  );
}

RHFAutocompleteMultiSelect.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  options: PropTypes.array,
  getOptionLabel: PropTypes.func,
};
